import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Image as ImageIcon, X } from 'react-feather';
import ModalShell from './ModalShell';
import { computeTrim } from './autoTrim';

const DEFAULT_VIEW_W = 472;
const MAX_VIEW_W = 472;
const MAX_VIEW_H = 360;
const MAX_ZOOM = 3;

// Splits an ImageAlignment enum value (e.g. "TOP_RIGHT") into vertical/horizontal anchors.
const parseAlignment = (alignment) => {
	const a = (alignment || 'CENTER_CENTER').toUpperCase();
	const vert = a.startsWith('TOP') ? 'TOP' : a.startsWith('BOTTOM') ? 'BOTTOM' : 'CENTER';
	const horiz = a.endsWith('LEFT') ? 'LEFT' : a.endsWith('RIGHT') ? 'RIGHT' : 'CENTER';
	return { vert, horiz };
};

const CropModal = ({ src, fileName, targetW = 696, targetH = 200, mimeType, alignment, maxLabel, freeCrop, onCancel, onConfirm }) => {
	const [nat, setNat] = useState(null);
	const [trimBox, setTrimBox] = useState(null); // content bounding box in source px, or null

	// Banner mode: fixed target size, wide frame. Free-crop mode (no dimension rule): the frame takes
	// the aspect ratio of the trimmed content box when there is one (so auto-trim can tighten BOTH
	// axes), else the image's own, and the output is whatever the frame shows at source pixel size.
	// The user can still zoom/drag to crop.
	const OUT_W = freeCrop ? (nat ? nat.w : targetW) : targetW;
	const OUT_H = freeCrop ? (nat ? nat.h : targetH) : targetH;
	const frameAspectSource = freeCrop && trimBox ? trimBox : { w: OUT_W, h: OUT_H };
	let VIEW_W = DEFAULT_VIEW_W;
	let VIEW_H = Math.max(40, Math.round((VIEW_W * OUT_H) / OUT_W));
	if (freeCrop) {
		const ar = frameAspectSource.w / frameAspectSource.h;
		VIEW_W = MAX_VIEW_W;
		VIEW_H = VIEW_W / ar;
		if (VIEW_H > MAX_VIEW_H) {
			VIEW_H = MAX_VIEW_H;
			VIEW_W = MAX_VIEW_H * ar;
		}
	}

	const [zoom, setZoom] = useState(1);
	const [pos, setPos] = useState({ x: 0, y: 0 });
	const [grabbing, setGrabbing] = useState(false);
	const dragRef = useRef(null);
	const imgRef = useRef(null);
	const autoFitDone = useRef(false);
	// A zoom+position pair to apply together. The clamp effect below runs on every zoom change, and if
	// it ran against a position set alongside a not-yet-rendered zoom it would clamp with the OLD
	// zoom's bounds (zero at 1×) and flatten the position. So a view change parks its position here
	// and the effect applies it only once the matching zoom has rendered.
	const pendingView = useRef(null);
	const touched = useRef(false); // flips true only on genuine user interaction (zoom/drag)

	const baseScale = nat ? Math.max(VIEW_W / nat.w, VIEW_H / nat.h) : 1;
	const containScale = nat ? Math.min(VIEW_W / nat.w, VIEW_H / nat.h) : 1;
	const minZoom = baseScale > 0 ? containScale / baseScale : 1;
	const s = baseScale * zoom;
	const dw = nat ? nat.w * s : VIEW_W;
	const dh = nat ? nat.h * s : VIEW_H;

	// Position for the contained (auto-fit) image, anchored per the ImageAlignment rule — mirrors the
	// server-side resizeAndAlignImage placement. Computed from containScale so it is independent of the
	// current zoom (and therefore stays correct even mid-transition).
	const alignedPos = useCallback(() => {
		if (!nat) return { x: 0, y: 0 };
		const cdw = nat.w * containScale;
		const cdh = nat.h * containScale;
		const xBound = Math.abs(VIEW_W - cdw) / 2;
		const yBound = Math.abs(VIEW_H - cdh) / 2;
		const { vert, horiz } = parseAlignment(alignment);
		return {
			x: horiz === 'LEFT' ? -xBound : horiz === 'RIGHT' ? xBound : 0,
			y: vert === 'TOP' ? -yBound : vert === 'BOTTOM' ? yBound : 0,
		};
	}, [nat, containScale, alignment, VIEW_W, VIEW_H]);

	// Zoom + position that contain-fits the trimmed content box inside the frame, with any slack on the
	// loose axis anchored per the alignment rule (same anchors as alignedPos). Returned zoom may exceed
	// MAX_ZOOM; the slider's max stretches to accommodate it.
	const trimView = useCallback(
		(box) => {
			if (!nat || !box) return null;
			const fit = Math.min(VIEW_W / box.w, VIEW_H / box.h);
			const z = fit / baseScale;
			const { vert, horiz } = parseAlignment(alignment);
			const slackX = (VIEW_W - box.w * fit) / 2;
			const slackY = (VIEW_H - box.h * fit) / 2;
			return {
				zoom: z,
				pos: {
					x: fit * (nat.w / 2 - box.x - box.w / 2) + (horiz === 'LEFT' ? -slackX : horiz === 'RIGHT' ? slackX : 0),
					y: fit * (nat.h / 2 - box.y - box.h / 2) + (vert === 'TOP' ? -slackY : vert === 'BOTTOM' ? slackY : 0),
				},
			};
		},
		[nat, baseScale, alignment, VIEW_W, VIEW_H]
	);

	// When the image is larger than the frame, clamp so the frame stays fully covered.
	// When smaller, clamp so the image stays fully inside the frame — same bound, just symmetric.
	const clamp = useCallback(
		(p) => {
			const xBound = Math.abs(VIEW_W - dw) / 2;
			const yBound = Math.abs(VIEW_H - dh) / 2;
			return {
				x: Math.max(-xBound, Math.min(xBound, p.x)),
				y: Math.max(-yBound, Math.min(yBound, p.y)),
			};
		},
		[dw, dh, VIEW_W, VIEW_H]
	);

	const applyView = useCallback(
		(view) => {
			if (view.zoom === zoom) {
				// Same zoom ⇒ no re-render to wait for, and the current clamp bounds already match.
				pendingView.current = null;
				setPos(clamp(view.pos));
				return;
			}
			pendingView.current = view;
			setZoom(view.zoom);
		},
		[zoom, clamp]
	);

	// On first image load: auto-trim by default. If a content box was found (onLoad sets it alongside
	// nat, so the frame is already shaped for it), zoom straight onto it; this counts as a crop, so the
	// file is re-encoded on upload. Otherwise contain-fit the full image, anchored per the alignment rule.
	useEffect(() => {
		if (nat && !autoFitDone.current) {
			autoFitDone.current = true;
			const view = trimView(trimBox);
			if (view) {
				touched.current = true;
				applyView(view);
			} else {
				applyView({ zoom: minZoom, pos: alignedPos() });
			}
		}
	}, [nat, trimBox, minZoom, trimView, alignedPos, applyView]);

	useEffect(() => {
		const pending = pendingView.current;
		if (pending) {
			if (zoom !== pending.zoom) return; // the new zoom hasn't rendered yet; wait for it
			pendingView.current = null;
			setPos(clamp(pending.pos));
		} else {
			setPos((p) => clamp(p));
		}
	}, [zoom, nat, clamp]);

	useEffect(() => {
		const onMove = (e) => {
			if (!dragRef.current) return;
			const pt = e.touches ? e.touches[0] : e;
			setPos(
				clamp({
					x: dragRef.current.ox + (pt.clientX - dragRef.current.sx),
					y: dragRef.current.oy + (pt.clientY - dragRef.current.sy),
				})
			);
		};
		const onUp = () => {
			dragRef.current = null;
			setGrabbing(false);
		};
		window.addEventListener('mousemove', onMove);
		window.addEventListener('mouseup', onUp);
		window.addEventListener('touchmove', onMove);
		window.addEventListener('touchend', onUp);
		return () => {
			window.removeEventListener('mousemove', onMove);
			window.removeEventListener('mouseup', onUp);
			window.removeEventListener('touchmove', onMove);
			window.removeEventListener('touchend', onUp);
		};
	}, [clamp]);

	const onDown = (e) => {
		const pt = e.touches ? e.touches[0] : e;
		dragRef.current = { sx: pt.clientX, sy: pt.clientY, ox: pos.x, oy: pos.y };
		touched.current = true;
		setGrabbing(true);
	};

	const left = (VIEW_W - dw) / 2 + pos.x;
	const top = (VIEW_H - dh) / 2 + pos.y;

	const confirm = () => {
		const img = imgRef.current;
		// Wait for the natural size: before onLoad, freeCrop OUT_W/OUT_H fall back to the banner size,
		// which would crop to the wrong dimensions.
		if (!img || !nat) {
			onConfirm(null);
			return;
		}
		// Free-crop with no user adjustment ⇒ keep the original file untouched (caller uploads it as-is
		// when no blob is returned), avoiding a needless re-encode.
		if (freeCrop && !touched.current) {
			onConfirm(null);
			return;
		}
		const canvas = document.createElement('canvas');
		canvas.width = OUT_W;
		canvas.height = OUT_H;
		const ctx = canvas.getContext('2d');
		// Keep the source format (png/jpeg) so the uploaded file's extension matches its content;
		// fall back to png for anything else.
		const outType = mimeType === 'image/jpeg' || mimeType === 'image/png' ? mimeType : 'image/png';
		// JPEG has no alpha channel — fill white so transparent areas don't render black. PNG keeps a
		// transparent background (so logo padding / transparency is preserved).
		if (outType === 'image/jpeg') {
			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, OUT_W, OUT_H);
		}
		try {
			if (freeCrop) {
				// Free crop: output the visible region at its SOURCE pixel size (a genuine trim, never an
				// upscale). After Reset the frame can extend past the image (its aspect follows the trim
				// box); the uncovered area stays transparent (PNG) or white (JPEG).
				const sx = -left / s;
				const sy = -top / s;
				const sw = VIEW_W / s;
				const sh = VIEW_H / s;
				canvas.width = Math.max(1, Math.round(sw));
				canvas.height = Math.max(1, Math.round(sh));
				if (outType === 'image/jpeg') {
					ctx.fillStyle = '#FFFFFF';
					ctx.fillRect(0, 0, canvas.width, canvas.height);
				}
				ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
			} else {
				const k = OUT_W / VIEW_W;
				ctx.drawImage(img, left * k, top * k, dw * k, dh * k);
			}
			canvas.toBlob(
				(blob) => {
					if (blob) onConfirm(blob);
					else onConfirm(null);
				},
				outType,
				0.95
			);
		} catch (err) {
			onConfirm(null);
		}
	};

	const reset = () => {
		touched.current = false; // back to the pristine full-image view
		applyView({ zoom: minZoom, pos: alignedPos() });
	};

	const autoTrim = () => {
		const view = trimView(trimBox);
		if (!view) return;
		touched.current = true;
		applyView(view);
	};

	const trimZoom = trimBox ? (trimView(trimBox) || {}).zoom || MAX_ZOOM : MAX_ZOOM;
	const maxZoom = Math.max(MAX_ZOOM, trimZoom);

	return (
		<ModalShell
			onClose={onCancel}
			ariaLabel="Position your logo"
			className="image-upload-crop"
			style={{ maxWidth: 560 }}
		>
			<div className="image-upload-modal__header">
				<div style={{ flex: 1 }}>
					<div className="image-upload-modal__title">{freeCrop ? 'Crop your image' : 'Position your logo'}</div>
					<div className="image-upload-modal__subtitle">
						{(trimBox ? 'Auto-trimmed · ' : '') +
							(freeCrop
								? `drag or zoom to crop · Reset for the full image`
								: `drag or zoom to fit the ${OUT_W}×${OUT_H} banner`) +
							(maxLabel ? ` · max ${maxLabel}` : '')}
					</div>
				</div>
				<button type="button" className="image-upload-modal__close" onClick={onCancel} aria-label="Close">
					<X size={19} />
				</button>
			</div>
			<div className="image-upload-crop__body">
				<div
					className={`image-upload-crop__frame${grabbing ? ' is-grabbing' : ''}`}
					style={{ width: VIEW_W, height: VIEW_H }}
					onMouseDown={onDown}
					onTouchStart={onDown}
				>
					<img
						ref={imgRef}
						src={src}
						alt=""
						draggable={false}
						crossOrigin="anonymous"
						onLoad={(e) => {
							const w = e.target.naturalWidth || OUT_W;
							const h = e.target.naturalHeight || OUT_H;
							// Set both in one event so the frame is shaped for the trim box on the same render.
							setTrimBox(computeTrim(e.target, w, h));
							setNat({ w, h });
						}}
						style={{ left, top, width: dw, height: dh }}
					/>
					<div className="image-upload-crop__frame-edge" />
				</div>
				<div className="image-upload-crop__zoom-row">
					<ImageIcon size={16} style={{ color: '#9AA1B3' }} aria-hidden="true" />
					<input
						type="range"
						min={minZoom.toFixed(3)}
						max={maxZoom.toFixed(3)}
						step="0.01"
						value={zoom}
						onChange={(e) => {
							touched.current = true;
							setZoom(parseFloat(e.target.value));
						}}
						aria-label="Zoom level"
					/>
					<span className="image-upload-crop__zoom-val">{zoom.toFixed(1)}×</span>
					<button
						type="button"
						className="image-upload-crop__reset"
						onClick={autoTrim}
						disabled={!trimBox}
						title={trimBox ? 'Zoom onto the logo, dropping the empty border' : 'Nothing to trim'}
					>
						Auto-trim
					</button>
					<button type="button" className="image-upload-crop__reset" onClick={reset} title="Show the full image">
						Reset
					</button>
				</div>
				{fileName && (
					<div
						style={{
							marginTop: 14,
							fontSize: '0.8rem',
							color: '#7C8398',
							textAlign: 'center',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
						title={fileName}
					>
						{fileName}
					</div>
				)}
			</div>
			<div className="image-upload-modal__footer" style={{ justifyContent: 'flex-end' }}>
				<button type="button" className="image-upload-modal__btn-secondary" onClick={onCancel}>
					Cancel
				</button>
				<button type="button" className="image-upload-modal__btn-primary" onClick={confirm}>
					<Upload size={16} /> Upload
				</button>
			</div>
		</ModalShell>
	);
};

export default CropModal;
